/* What a checkout is allowed to say about when a service ended.
 *
 * A sale closing is the end of a VISIT, not of anybody's work. A visit with a
 * pedicure and a manicure is two technicians and two squares, and the one who
 * finished first was free from the moment she stood up — not from when the
 * client, who stayed for the other service or simply sat and chatted, got
 * round to paying. So the close time is a fallback for squares nobody stopped,
 * and never a correction of one somebody did:
 *
 *   a square already stopped on the grid keeps its time, however it got it —
 *   the stop button, a time typed in, or a square that went up finished;
 *
 *   a square still running when the ticket closes is stopped at the close,
 *   because that is the latest the service can have gone on and nobody said
 *   otherwise;
 *
 *   a square that started after the close is not about that sale at all.
 *
 * The same reasoning decides whether a close may stand in for one service's
 * end when a booking is written up after the fact: only when the visit had
 * one service, so the close and the service are the same thing.
 */

/* When the checkout stops this square, or null to leave it alone.
 *
 *   turn      { startedAt, endedAt } — endedAt null while it runs.
 *   closedAt  when the ticket for its appointment closed, or null.
 */
export function checkoutEnd(turn, closedAt) {
  if (!turn || turn.endedAt != null) return null;   // stopped already: theirs
  if (!closedAt) return null;
  // A close that predates the square cannot be about it — a square linked to
  // an appointment settled earlier in the day. Honouring it would stop the
  // turn on the spot and record a 0:00 service.
  if (closedAt < turn.startedAt) return null;
  return closedAt;
}

/* When one booked service ended, for writing it up after the fact.
 *
 *   closedAt         the visit's ticket close, or null.
 *   startsAt         the service's booked start.
 *   durationMinutes  its booked length, or 0 when the book has none.
 *   servicesOnVisit  how many services the visit has.
 *
 * The close beats the booked length only for a one-service visit. Null when
 * there is nothing to go on.
 */
export function bookedServiceEnd(closedAt, startsAt, durationMinutes, servicesOnVisit) {
  if (servicesOnVisit === 1 && closedAt && closedAt > startsAt) return closedAt;
  if (!durationMinutes) return null;
  return startsAt + durationMinutes * 60000;
}
